import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useRewardsToast } from '@/features/guide-rewards/components/RewardsToast';
import { adminErrorMessage, adminGrowthKeys } from '../api/admin-growth.api';

export const fmtDate = (value?: string | null) => (value ? dayjs(value).format('D MMM YYYY') : '—');
export const fmtDateTime = (value?: string | null) => (value ? dayjs(value).format('D MMM YYYY, h:mm A') : '—');
export const fmtNum = (value?: number | null) => (value ?? 0).toLocaleString('en-IN');
export const humanize = (value: string) =>
  value.toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** Any admin growth write can move counts across tabs, so refresh the section. */
export function useGrowthMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  successMessage: string
) {
  const queryClient = useQueryClient();
  const notify = useRewardsToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      notify(successMessage);
      void queryClient.invalidateQueries({ queryKey: adminGrowthKeys.all });
    },
    onError: (error) => notify(adminErrorMessage(error), 'error'),
  });
}

export interface Column<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
}

export function GrowthTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  emptyText,
  onRowClick,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
}: {
  columns: Column<T>[];
  rows?: T[];
  rowKey: (row: T) => string;
  loading: boolean;
  error?: boolean;
  emptyText: string;
  onRowClick?: (row: T) => void;
  page?: number;
  limit?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}) {
  if (error) return <Alert severity="error" sx={{ borderRadius: 0 }}>Could not load this list. Refresh to try again.</Alert>;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: col.width, bgcolor: 'background.default' }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [0, 1, 2, 3].map((i) => (
                <TableRow key={i}>
                  <TableCell colSpan={columns.length}>
                    <Skeleton height={28} />
                  </TableCell>
                </TableRow>
              ))
            ) : !rows?.length ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  hover={Boolean(onRowClick)}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={onRowClick ? (e) => e.key === 'Enter' && onRowClick(row) : undefined}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align}>
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {onPageChange && page !== undefined && limit !== undefined && (
        <TablePagination
          component="div"
          count={total ?? 0}
          page={page}
          rowsPerPage={limit}
          onPageChange={(_e, next) => onPageChange(next)}
          onRowsPerPageChange={(e) => onLimitChange?.(Number(e.target.value))}
          rowsPerPageOptions={[20, 50, 100]}
        />
      )}
    </Box>
  );
}

/** Confirmation that requires (or optionally takes) a written reason. */
export function ReasonDialog({
  open,
  title,
  description,
  confirmLabel,
  required = true,
  danger,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  required?: boolean;
  danger?: boolean;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const tooShort = required && reason.trim().length < 3;

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label={required ? 'Reason' : 'Note (optional)'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={danger ? 'error' : 'primary'}
          disabled={tooShort || pending}
          onClick={() => onConfirm(reason.trim())}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function FilterBar({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2, '& .MuiTextField-root': { minWidth: 160 } }}>
      {children}
    </Box>
  );
}

export function Metric({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.03em', mt: 0.25 }}>{value}</Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Box>
  );
}
